import React, { useEffect, useState } from 'react';
import { EventTicketSetup } from '../../../api/playguides';
import DsButton from '../../common/DsButton';
import DsModal from '../../common/DsModal';
import EventPerformanceScopeSelect from '../../common/events/EventPerformanceScopeSelect';

type Props = {
  open: boolean;
  tickets: EventTicketSetup['tickets'];
  performances: EventTicketSetup['performances'];
  saving?: boolean;
  onClose: () => void;
  onSave: (tickets: EventTicketSetup['tickets']) => void | Promise<void>;
};

export default function EventTicketTypesModal({
  open,
  tickets,
  performances,
  saving = false,
  onClose,
  onSave,
}: Props) {
  const [draft, setDraft] = useState<EventTicketSetup['tickets']>([]);

  useEffect(() => {
    if (open) setDraft(tickets.map((ticket) => ({ ...ticket })));
  }, [open, tickets]);

  const patchTicket = (index: number, patch: Partial<EventTicketSetup['tickets'][number]>) => {
    setDraft((current) => current.map((ticket, ticketIndex) => (
      ticketIndex === index ? { ...ticket, ...patch } : ticket
    )));
  };

  return (
    <DsModal
      open={open}
      onClose={onClose}
      title="チケット種別を設定"
      ariaLabel="チケット種別を設定"
      size="lg"
      className="event-ticket-setup-submodal"
      overlayClassName="event-ticket-setup-submodal__overlay"
      dismissible={!saving}
      footer={(
        <DsButton loading={saving} onClick={() => void onSave(draft)}>
          チケット種別を保存
        </DsButton>
      )}
    >
      <p className="event-ticket-setup-submodal__lead">
        事前に登録する場合だけ設定してください。外部サイトから取得した券種を、連動時に新しい券種として追加することもできます。
      </p>
      <div className="event-ticket-setup-flow__ticket-list">
        {draft.map((ticket, index) => (
          <div
            className={`event-ticket-setup-flow__ticket-row${performances.length > 1 ? ' has-performance' : ''}`}
            key={ticket.id || `new-${index}`}
          >
            <input
              aria-label={`チケット名 ${index + 1}`}
              value={ticket.ticketType}
              onChange={(event) => patchTicket(index, { ticketType: event.target.value })}
              placeholder="チケット名"
            />
            {performances.length > 1 ? (
              <EventPerformanceScopeSelect
                performances={performances.map((performance) => ({
                  id: performance.id,
                  name: performance.name,
                  date: performance.date,
                  startTime: performance.startTime,
                }))}
                value={ticket.eventPerformanceId === undefined
                  ? ''
                  : ticket.eventPerformanceId === null
                    ? 'event'
                    : `id:${ticket.eventPerformanceId}`}
                ariaLabel={`対象 ${index + 1}`}
                includeUnselected
                onChange={(value) => patchTicket(index, {
                  eventPerformanceId: value === 'event'
                    ? null
                    : Number(value.replace(/^id:/, '')),
                })}
              />
            ) : null}
            <input
              aria-label={`金額 ${index + 1}`}
              type="number"
              min="0"
              value={ticket.price}
              onChange={(event) => patchTicket(index, { price: Number(event.target.value) })}
              placeholder="金額（円）"
            />
            <input
              aria-label={`上限枚数 ${index + 1}`}
              type="number"
              min={ticket.sold || 0}
              value={ticket.quantity}
              onChange={(event) => patchTicket(index, { quantity: Number(event.target.value) })}
              placeholder="上限枚数"
            />
            <DsButton
              variant="danger"
              size="sm"
              disabled={Number(ticket.sold || 0) > 0}
              onClick={() => setDraft((current) => current.filter((_, ticketIndex) => ticketIndex !== index))}
            >
              削除
            </DsButton>
          </div>
        ))}
        <DsButton
          variant="secondary"
          size="sm"
          onClick={() => setDraft((current) => [...current, {
            ticketType: '',
            price: 0,
            quantity: 0,
            sold: 0,
            eventPerformanceId: performances.length > 1 ? undefined : null,
          }])}
        >
          チケット種別を追加
        </DsButton>
      </div>
    </DsModal>
  );
}
